import React, { useState, useCallback, useMemo } from 'react';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  FolderOpen, Plus, ChevronRight, ChevronDown, Edit2, Trash2,
  Loader2, GripVertical, Image, Save, X
} from 'lucide-react';
import { partCategoriesAPI } from '../lib/api';

// ─── Tree Node ───
function CategoryNode({ node, depth, staged, onUpdate, onDelete, onAddChild }) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(node.name);
  const [editThumb, setEditThumb] = useState(node.thumbnail_url || '');
  const [addingChild, setAddingChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');

  const hasChildren = (node.children || []).length > 0;
  const isNew = (node.id || '').startsWith('temp_');

  const handleSaveEdit = () => {
    if (!editName.trim()) return;
    onUpdate(node.id, { name: editName.trim(), thumbnail_url: editThumb || null });
    setEditing(false);
  };

  const handleAddChild = () => {
    if (!newChildName.trim()) return;
    onAddChild(node.id, newChildName.trim());
    setNewChildName('');
    setAddingChild(false);
    setExpanded(true);
  };

  return (
    <div data-testid={`cat-node-${node.id}`}>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md hover:bg-muted/50 group ${
          isNew ? 'bg-green-50 dark:bg-green-950/20' : ''
        }`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`w-5 h-5 flex items-center justify-center shrink-0 ${
            hasChildren ? 'text-muted-foreground' : 'invisible'
          }`}
          data-testid={`cat-toggle-${node.id}`}
        >
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        {/* Thumbnail */}
        <div className="w-6 h-6 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
          {node.thumbnail_url ? (
            <img src={node.thumbnail_url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <FolderOpen className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </div>

        {editing ? (
          <div className="flex-1 flex items-center gap-1.5">
            <Input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditing(false); }}
              className="h-7 text-sm flex-1"
              autoFocus
            />
            <Input
              value={editThumb}
              onChange={e => setEditThumb(e.target.value)}
              placeholder="Image URL"
              className="h-7 text-xs w-32"
            />
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleSaveEdit}>
              <Save className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(false)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-sm font-medium truncate">{node.name}</span>
            {node.item_count > 0 && (
              <span className="text-xs text-muted-foreground tabular-nums">{node.item_count} items</span>
            )}
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title="Add sub category"
                onClick={() => setAddingChild(!addingChild)} data-testid={`cat-add-child-${node.id}`}>
                <Plus className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title="Edit"
                onClick={() => { setEditing(true); setEditName(node.name); setEditThumb(node.thumbnail_url || ''); }}
                data-testid={`cat-edit-${node.id}`}>
                <Edit2 className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive hover:text-destructive" title="Delete"
                onClick={() => onDelete(node.id, node.name, hasChildren)} data-testid={`cat-delete-${node.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Inline add child */}
      {addingChild && (
        <div className="flex items-center gap-1.5 py-1" style={{ paddingLeft: `${(depth + 1) * 20 + 8}px` }}>
          <Input
            value={newChildName}
            onChange={e => setNewChildName(e.target.value)}
            placeholder="Sub category name"
            className="h-7 text-sm flex-1"
            onKeyDown={e => { if (e.key === 'Enter') handleAddChild(); if (e.key === 'Escape') setAddingChild(false); }}
            autoFocus
          />
          <Button size="sm" className="h-7" onClick={handleAddChild} disabled={!newChildName.trim()}>
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setAddingChild(false)}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <CategoryNode
              key={child.id}
              node={child}
              depth={depth + 1}
              staged={staged}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Drawer ───
let tempCounter = 0;

export function CategoryManagerDrawer({ open, onOpenChange, onSaved }) {
  const [tree, setTree] = useState([]);
  const [originalTree, setOriginalTree] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRootName, setNewRootName] = useState('');

  // Deep-clone helper
  const clone = useCallback((t) => JSON.parse(JSON.stringify(t)), []);

  // Fetch tree on open
  React.useEffect(() => {
    if (open) {
      setLoading(true);
      setDeletedIds([]);
      tempCounter = 0;
      partCategoriesAPI.tree()
        .then(res => {
          setTree(res.data);
          setOriginalTree(clone(res.data));
        })
        .catch(() => toast.error('Failed to load categories'))
        .finally(() => setLoading(false));
    }
  }, [open, clone]);

  const isDirty = useMemo(
    () => JSON.stringify(tree) !== JSON.stringify(originalTree) || deletedIds.length > 0,
    [tree, originalTree, deletedIds]
  );

  // ─── Tree manipulation helpers (all local, staged) ───

  const updateNodeInTree = useCallback((nodes, id, updates) => {
    return nodes.map(n => {
      if (n.id === id) return { ...n, ...updates };
      if (n.children?.length) return { ...n, children: updateNodeInTree(n.children, id, updates) };
      return n;
    });
  }, []);

  const removeNodeFromTree = useCallback((nodes, id) => {
    return nodes.filter(n => n.id !== id).map(n => ({
      ...n,
      children: n.children?.length ? removeNodeFromTree(n.children, id) : [],
    }));
  }, []);

  const addChildToNode = useCallback((nodes, parentId, child) => {
    return nodes.map(n => {
      if (n.id === parentId) {
        return { ...n, children: [...(n.children || []), child] };
      }
      if (n.children?.length) return { ...n, children: addChildToNode(n.children, parentId, child) };
      return n;
    });
  }, []);

  const collectAllIds = useCallback((nodes) => {
    let ids = [];
    for (const n of nodes) {
      ids.push(n.id);
      if (n.children?.length) ids = ids.concat(collectAllIds(n.children));
    }
    return ids;
  }, []);

  // ─── Actions ───

  const handleUpdate = useCallback((id, updates) => {
    setTree(prev => updateNodeInTree(prev, id, updates));
  }, [updateNodeInTree]);

  const handleDelete = useCallback((id, name, hasChildren) => {
    if (hasChildren) {
      if (!window.confirm(`"${name}" has sub categories. Delete the entire subtree?`)) return;
    }
    // Collect all IDs in subtree to mark for deletion
    const findNode = (nodes) => {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children?.length) {
          const found = findNode(n.children);
          if (found) return found;
        }
      }
      return null;
    };
    const node = findNode(tree);
    if (node) {
      const idsToDelete = collectAllIds([node]).filter(i => !i.startsWith('temp_'));
      setDeletedIds(prev => [...prev, ...idsToDelete]);
    }
    setTree(prev => removeNodeFromTree(prev, id));
  }, [tree, removeNodeFromTree, collectAllIds]);

  const handleAddChild = useCallback((parentId, name) => {
    const newId = `temp_${++tempCounter}`;
    const child = {
      id: newId,
      name,
      parent_id: parentId,
      thumbnail_url: null,
      sort_order: 0,
      item_count: 0,
      child_count: 0,
      children: [],
    };
    setTree(prev => addChildToNode(prev, parentId, child));
  }, [addChildToNode]);

  const handleAddRoot = useCallback(() => {
    if (!newRootName.trim()) return;
    const newId = `temp_${++tempCounter}`;
    setTree(prev => [...prev, {
      id: newId,
      name: newRootName.trim(),
      parent_id: null,
      thumbnail_url: null,
      sort_order: prev.length,
      item_count: 0,
      child_count: 0,
      children: [],
    }]);
    setNewRootName('');
  }, [newRootName]);

  // ─── Flatten tree for bulk save ───
  const flattenTree = useCallback((nodes, parentId = null) => {
    let flat = [];
    nodes.forEach((n, i) => {
      flat.push({
        id: n.id.startsWith('temp_') ? null : n.id,
        name: n.name,
        parent_id: parentId,
        thumbnail_url: n.thumbnail_url,
        sort_order: i,
      });
      if (n.children?.length) {
        flat = flat.concat(flattenTree(n.children, n.id.startsWith('temp_') ? null : n.id));
      }
    });
    return flat;
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // Use individual create/update/delete calls for proper parent_id resolution
      // First: delete removed categories
      for (const id of deletedIds) {
        try {
          await partCategoriesAPI.delete(id, true);
        } catch { /* already deleted */ }
      }

      // Then: create new + update existing using the tree endpoint to rebuild
      const flat = flattenTree(tree);
      await partCategoriesAPI.bulkSave(flat, []);

      toast.success('Categories saved');
      onSaved?.();
      // Refresh tree from server
      const res = await partCategoriesAPI.tree();
      setTree(res.data);
      setOriginalTree(clone(res.data));
      setDeletedIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save categories');
    } finally {
      setSaving(false);
    }
  }, [tree, deletedIds, flattenTree, clone, onSaved]);

  const handleCancel = useCallback(() => {
    setTree(clone(originalTree));
    setDeletedIds([]);
    toast('Changes discarded');
    onOpenChange(false);
  }, [clone, originalTree, onOpenChange]);

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Manage Categories"
      description="Organize your catalogue with main categories and sub categories. Changes are staged until you click Save."
      size="md"
      isDirty={isDirty}
      data-testid="category-manager-drawer"
      footer={
        <>
          <Button variant="outline" onClick={handleCancel} disabled={saving} data-testid="cat-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !isDirty} data-testid="cat-drawer-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Add Main Category */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              Add Main Category
            </label>
            <div className="flex gap-2">
              <Input
                value={newRootName}
                onChange={e => setNewRootName(e.target.value)}
                placeholder="e.g. Garage Door Parts, HVAC, Plumbing"
                className="flex-1 h-9"
                onKeyDown={e => { if (e.key === 'Enter') handleAddRoot(); }}
                data-testid="new-root-category-input"
              />
              <Button onClick={handleAddRoot} disabled={!newRootName.trim()} size="sm" className="h-9"
                data-testid="new-root-category-add">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Tree */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              Category Hierarchy
            </label>
            {tree.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground" data-testid="cat-tree-empty">
                <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">No categories yet</p>
                <p className="text-xs mt-1">Add a main category above to get started.</p>
              </div>
            ) : (
              <div className="border rounded-lg divide-y-0 py-1" data-testid="cat-tree">
                {tree.map(node => (
                  <CategoryNode
                    key={node.id}
                    node={node}
                    depth={0}
                    staged
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onAddChild={handleAddChild}
                  />
                ))}
              </div>
            )}
          </div>

          {isDirty && (
            <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded px-3 py-2" data-testid="unsaved-indicator">
              You have unsaved changes. Click Save to apply or Cancel to discard.
            </p>
          )}
        </div>
      )}
    </AppDrawer>
  );
}
